/**
 * @file themeContext.ts
 * @brief The theme context object and its `useTheme` consumer hook.
 *
 * @details
 * Split out of `ThemeProvider.tsx` (02-08-2026). The provider is a component;
 * a module that exports **both** a component and a value (a hook is a value)
 * loses React Fast Refresh — `@vitejs/plugin-react` can no longer hot-swap it,
 * so every edit to the provider full-reloaded the page. Context + hook live
 * here, the provider stays in the `.tsx`, and both sides keep Fast Refresh.
 *
 * Import `useTheme` from here; import `ThemeProvider` from `ThemeProvider.tsx`.
 *
 * @references
 * - shared/theme/ThemeProvider.tsx
 *
 * @author DES
 * @date 02-08-2026
 */

import { createContext, useContext } from "react";

export type Theme = "light" | "dark" | "system";

export interface ThemeContextValue {
  theme: Theme;
  resolvedTheme: "light" | "dark";
  setTheme: (theme: Theme) => void;
}

export const ThemeContext = createContext<ThemeContextValue | undefined>(
  undefined,
);

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error("useTheme must be used within <ThemeProvider>");
  }
  return ctx;
}
